/* istanbul ignore file */

import { Injectable } from "@angular/core";
import { AuthService } from "./auth.service";
import { environment } from "@environment";
import {
  applyActionCode,
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  User,
  Auth,
} from "@angular/fire/auth";
import {
  catchError,
  from,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  timer,
} from "rxjs";
import { Firestore } from "@angular/fire/firestore";
import { FirebaseError } from "@angular/fire/app";

import { AUTH_ERRORS } from "@app/model";
import {
  Functions,
  httpsCallable,
  HttpsCallable,
  FunctionsError,
} from "@angular/fire/functions";

export type TotpSession = {
  secret: TotpSecret;
  qrCodeUrl: string;
  keyToDisplay: string;
  currentUser: User;
};

export type VerifyEmailCheckResponseError =
  | "joymo-auth/email-verify-code-expired"
  | "joymo-auth/email-verify-wrong-code"
  | "joymo-auth/email-verify-too-many-attempts"
  | "joymo-auth/email-verify-unknown-error";

export type VerifyEmailCheckResponse =
  | { success: true }
  | {
      success: false;
      error: VerifyEmailCheckResponseError;
    };

export interface EmailVerificationData {
  action: "emailVerification";
  code: string;
}

export interface CheckMfaResponse {
  isMfaEnabled: boolean;
  isEmailVerified: boolean;
}

export type CheckMfaCallable = HttpsCallable<
  EmailVerificationData | {},
  CheckMfaResponse
>;

@Injectable({
  providedIn: "root",
})
export class MfaService {
  private checkMFAFunction: CheckMfaCallable;

  constructor(
    protected authService: AuthService,
    protected auth: Auth,
    protected fs: Firestore,
    protected functions: Functions
  ) {
    this.checkMFAFunction = httpsCallable(functions, "users-checkmfa");
  }

  initSession(type: "totp"): Observable<TotpSession> {
    return this.authService.getUser$(true).pipe(
      take(1),
      switchMap(async (currentUser) => {
        const multiFactorSession = await multiFactor(currentUser).getSession();
        const totpSecret = await TotpMultiFactorGenerator.generateSecret(
          multiFactorSession
        );
        return {
          secret: totpSecret,
          qrCodeUrl: totpSecret.generateQrCodeUrl(
            currentUser.email || currentUser.displayName || "",
            environment.meta.mfaAppName
          ),
          keyToDisplay: totpSecret.secretKey,
          currentUser,
        };
      })
    );
  }

  finalizeEnrollment(
    code: string,
    session: TotpSession
  ): Observable<
    | { success: true }
    | { success: false; error: keyof typeof AUTH_ERRORS; canRetry: boolean }
  > {
    const enrollment = async () => {
      try {
        const multiFactorAssertion =
          TotpMultiFactorGenerator.assertionForEnrollment(session.secret, code);
        await multiFactor(session.currentUser).enroll(
          multiFactorAssertion,
          "TOTP"
        );
      } catch (e) {
        if (!(e instanceof FirebaseError)) {
          return {
            success: false,
            error: "joymo-auth/totp-enrollment-failed",
            canRetry: false,
          } as const;
        }
        if (e.code === "auth/invalid-verification-code") {
          return {
            success: false,
            error: "auth/invalid-verification-code",
            canRetry: true,
          } as const;
        }
      }
      return {
        success: true,
      } as const;
    };
    return from(enrollment());
  }

  async resetMFA(resetCode: string): Promise<void> {
    await applyActionCode(this.auth, resetCode);
  }

  mfaRequiredForUser(): Observable<{
    mfaRequired: boolean;
    verifyRequired: boolean;
  }> {
    return this.authService.getUser$(true).pipe(
      take(1),
      map(
        (user) =>
          [user, multiFactor(user).enrolledFactors.length !== 0] as const
      ),
      switchMap(([user, mfaAlreadyEnabled]) => {
        if (mfaAlreadyEnabled) {
          return of({ user, mfaRequired: false, verifyRequired: false });
        }
        return from(this.checkMFAFunction({})).pipe(
          map((result) => {
            return {
              user,
              mfaRequired: result.data.isMfaEnabled,
              verifyRequired:
                result.data.isMfaEnabled && !result.data.isEmailVerified,
            };
          })
        );
      }),
      switchMap((result) => {
        if (result.verifyRequired) {
          if (!result.user.email) {
            throw new Error(
              "Can't verify email. No Email attached to this user"
            );
          }
          return this.authService
            .requestEmailVerification$("STUDIO", result.user.email)
            .pipe(map((_) => result));
        }
        return of(result);
      })
    );
  }

  verifyEmail(code: string): Observable<VerifyEmailCheckResponse> {
    return from(
      this.checkMFAFunction({ action: "emailVerification", code })
    ).pipe(
      map((r) => {
        if (r.data.isEmailVerified) {
          return { success: true } satisfies VerifyEmailCheckResponse;
        } else {
          return {
            success: false,
            error: "joymo-auth/email-verify-wrong-code",
          } satisfies VerifyEmailCheckResponse;
        }
      }),
      catchError((err) => {
        let error: VerifyEmailCheckResponseError =
          "joymo-auth/email-verify-unknown-error";
        if (err instanceof FirebaseError) {
          switch (err.message) {
            case "The email verification code has expired":
              error = "joymo-auth/email-verify-code-expired";
              break;
            case "The email verification code is invalid":
              error = "joymo-auth/email-verify-wrong-code";
              break;
            case "NotImplementedYet":
              error = "joymo-auth/email-verify-too-many-attempts";
              break;
          }
        }

        return of({
          success: false,
          error,
        } satisfies VerifyEmailCheckResponse);
      })
    );
  }
}
